import { Anchor, Autocomplete, Fieldset, Loader, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContactImageField } from 'src/Components/Forms/Contact/PhysicalContactForm/ContactImageField';
import useSearchCompanies from 'src/Hooks/Queries/useSearchCompanies';
import { CompanySearchResultDto } from 'src/Services/API/generated';
import {
  trackContactCompanySearchManual,
  trackContactCompanySearchSelected,
} from 'src/Services/Mixpanel/events';
import { formatSiret } from 'src/Utils/companyContactRoles';

import styles from './CompanySectionForm.module.scss';
import { Props } from './CompanySectionForm.types';

const optionLabel = (company: CompanySearchResultDto) =>
  [company.name, [company.postalCode, company.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' — ');

const CompanySectionForm = ({ form, onCompanySelected, autoFocusName, header }: Props) => {
  const { t } = useTranslation();
  const manualOption = t('contacts.company.manualEntry', 'Je ne la trouve pas, je saisis le nom');

  // Name the user picked or confirmed: no new search until they type something else.
  const [settledName, setSettledName] = useState<string | undefined>(
    form.values.siretFound ? form.values.companyName : undefined
  );

  const [debouncedName] = useDebouncedValue(form.values.companyName, 400);
  const searchEnabled = !!debouncedName.trim() && debouncedName !== settledName;
  const { data: results, isFetching } = useSearchCompanies(searchEnabled ? debouncedName : '');

  const optionsByLabel = useMemo(
    () => new Map((results ?? []).map(company => [optionLabel(company), company])),
    [results]
  );

  const showSuggestions = searchEnabled && form.values.companyName === debouncedName;
  const dropdownData = showSuggestions
    ? [...optionsByLabel.keys(), ...(results ? [manualOption] : [])]
    : [];

  const handleNameChange = (value: string) => {
    const company = optionsByLabel.get(value);
    if (company) {
      form.setFieldValue('companyName', company.name);
      form.setFieldValue('siret', company.siret);
      form.setFieldValue('siretFound', true);
      setSettledName(company.name);
      onCompanySelected(company);
      trackContactCompanySearchSelected();
      return;
    }
    if (value === manualOption) {
      setSettledName(form.values.companyName);
      trackContactCompanySearchManual();
      return;
    }
    form.setFieldValue('companyName', value);
  };

  return (
    <Fieldset legend={t('contacts.company.section', 'Entreprise')}>
      <div className={styles.content}>
        {header}
        <div>
          <Autocomplete
            label={t('contacts.company.name', "Nom de l'entreprise ou de l'organisme")}
            placeholder={t('contacts.company.namePlaceholder', 'Ex. Fessart Immobilier, EDF…')}
            required
            autoFocus={autoFocusName}
            leftSection={<IconSearch size={16} />}
            rightSection={isFetching ? <Loader size="xs" /> : null}
            data={dropdownData}
            filter={({ options }) => options}
            value={form.values.companyName}
            onChange={handleNameChange}
            error={form.errors.companyName}
            comboboxProps={{ withinPortal: true }}
          />
          {showSuggestions && optionsByLabel.size > 0 && (
            <Text size="xs" c="dimmed" mt={4}>
              {t('contacts.company.source', 'Source : Annuaire des Entreprises (data.gouv.fr)')}
            </Text>
          )}
        </div>

        {form.values.siretFound ? (
          <Text size="sm" className={styles.siretFound}>
            {t('contacts.company.siretFound', 'SIRET {{siret}} · trouvé automatiquement', {
              siret: formatSiret(form.values.siret),
            })}{' '}
            <Anchor
              component="button"
              type="button"
              size="sm"
              onClick={() => form.setFieldValue('siretFound', false)}
            >
              {t('contacts.company.editSiret', 'Modifier')}
            </Anchor>
          </Text>
        ) : (
          <TextInput
            label={t('contacts.company.siret', 'SIRET')}
            placeholder={t('contacts.company.siretPlaceholder', '14 chiffres')}
            inputMode="numeric"
            {...form.getInputProps('siret')}
          />
        )}

        <ContactImageField form={form} label={t('contacts.company.logo', 'Logo ou photo')} />
      </div>
    </Fieldset>
  );
};

export default CompanySectionForm;
